import { useState, useCallback } from 'react';

/**
 * Custom hook for managing GlovoModal state
 * Simplifies modal data, loading, and error management
 * 
 * @param {string} defaultTitle - Default modal title
 * @returns {Object} Modal state and control functions
 * 
 * @example
 * const { 
 *   modalData, 
 *   modalLoading, 
 *   modalError, 
 *   modalTitle,
 *   openModal, 
 *   closeModal,
 *   showLoading,
 *   showError,
 *   showData
 * } = useGlovoModal('Default Title');
 * 
 * // Show loading
 * showLoading('Fetching products...');
 * 
 * // Show data
 * const data = await fetchProducts();
 * showData('Products', data);
 * 
 * // Show error
 * showError('Error occurred', errorMessage);
 */
export const useGlovoModal = (defaultTitle = 'Glovo API Response') => {
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [modalTitle, setModalTitle] = useState(defaultTitle);

  /**
   * Open modal with specific state
   */
  const openModal = useCallback((title, data = null, loading = false, error = null) => {
    setModalTitle(title || defaultTitle);
    setModalData(data);
    setModalLoading(loading);
    setModalError(error);
    
    // Trigger Bootstrap modal
    const modalElement = document.getElementById('glovoModal');
    if (modalElement && window.bootstrap) {
      const modal = new window.bootstrap.Modal(modalElement);
      modal.show();
    }
  }, [defaultTitle]);

  /**
   * Close modal and reset state
   */
  const closeModal = useCallback(() => {
    setModalData(null);
    setModalLoading(false);
    setModalError(null);
    setModalTitle(defaultTitle);
    
    // Programmatically close if needed
    const modalElement = document.getElementById('glovoModal');
    if (modalElement && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }, [defaultTitle]);

  /**
   * Show loading state
   */
  const showLoading = useCallback((title = 'Chargement...') => {
    openModal(title, null, true, null);
  }, [openModal]);

  /**
   * Show data in modal
   */
  const showData = useCallback((title, data) => {
    openModal(title, data, false, null);
  }, [openModal]);

  /**
   * Show error in modal
   */
  const showError = useCallback((title, error) => {
    const errorMessage = typeof error === 'string' 
      ? error 
      : error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Une erreur est survenue';
    openModal(title, null, false, errorMessage);
  }, [openModal]);

  /**
   * Execute async function with automatic loading/error handling
   */
  const executeWithModal = useCallback(async (title, asyncFn) => {
    showLoading(title);
    try {
      const result = await asyncFn();
      showData(title, result);
      return result;
    } catch (error) {
      console.error(`${title} error:`, error);
      showError(`Erreur - ${title}`, error);
      throw error;
    }
  }, [showLoading, showData, showError]);

  return {
    // State
    modalData,
    modalLoading,
    modalError,
    modalTitle,
    
    // Controls
    openModal,
    closeModal,
    
    // Helpers
    showLoading,
    showData,
    showError,
    executeWithModal,
    
    // Setters (for advanced use)
    setModalData,
    setModalLoading,
    setModalError,
    setModalTitle,
  };
};

export default useGlovoModal;
